import React, { useEffect, useState } from "react";
import { Clock, ExternalLink, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

/**
 * TrialBanner — persistent banner on the Employer Dashboard that shows
 * days-left on an active trial and offers one-click "add a payment
 * method" via the Stripe customer portal.
 *
 * Only renders when the backend reports ``is_trialing=true``. Silent
 * otherwise — including for Free-plan employers who never started a
 * trial, past-trial canceled accounts (handled by the Billing page),
 * and paid customers.
 *
 * Tone escalates with urgency:
 *   • >7 days left → neutral emerald
 *   • 4-7 days   → amber
 *   • ≤3 days    → rose (same cadence as the reminder email)
 */
export const TrialBanner = () => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [portalBusy, setPortalBusy] = useState(false);

    useEffect(() => {
        let active = true;
        api.get("/employer/trial-status")
            .then((r) => { if (active) setStatus(r.data); })
            .catch(() => { /* non-critical — just hide */ })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    const openPortal = async () => {
        try {
            setPortalBusy(true);
            const { data } = await api.post("/billing/customer-portal");
            window.location.assign(data.url);
        } catch (err) {
            toast.error(formatApiError(err));
            setPortalBusy(false);
        }
    };

    if (loading || !status || !status.is_trialing) return null;

    const days = status.days_remaining;
    const plan = status.plan_label || "Starter";

    // Tone + microcopy escalate as the deadline nears so the banner
    // actually catches the eye on day 2 instead of blending in.
    let tone;
    if (days !== null && days <= 3) {
        tone = {
            border: "border-rose-500/50",
            bg: "bg-rose-500/10",
            icon: "text-rose-600 dark:text-rose-400",
            btn: "bg-rose-600 hover:bg-rose-700",
            headline: `${days} day${days === 1 ? "" : "s"} left on your ${plan} trial`,
            body: "Your jobs will close back to the Free plan if no payment method is on file by then. Add one now to keep publishing.",
        };
    } else if (days !== null && days <= 7) {
        tone = {
            border: "border-amber-500/50",
            bg: "bg-amber-500/10",
            icon: "text-amber-600 dark:text-amber-400",
            btn: "bg-amber-600 hover:bg-amber-700",
            headline: `${days} days left on your ${plan} trial`,
            body: "Add a payment method before your trial ends so your subscription continues without interruption.",
        };
    } else {
        tone = {
            border: "border-emerald-500/50",
            bg: "bg-emerald-500/5",
            icon: "text-emerald-600 dark:text-emerald-400",
            btn: "bg-emerald-600 hover:bg-emerald-700",
            headline: days !== null
                ? `${days} days left on your ${plan} trial`
                : `${plan} trial active`,
            body: "You're on the free trial — you can add a payment method whenever you like, or cancel in under a minute.",
        };
    }

    return (
        <div
            className={`mb-4 rounded-xl border ${tone.border} ${tone.bg} p-4 flex items-start gap-3 flex-wrap sm:flex-nowrap`}
            data-testid="employer-trial-banner"
            data-days-remaining={days ?? ""}
        >
            <div className={`shrink-0 h-10 w-10 rounded-xl bg-background flex items-center justify-center ${tone.icon}`}>
                <Clock size={18} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="font-heading font-semibold text-foreground text-sm sm:text-base" data-testid="trial-banner-headline">
                    {tone.headline}
                </div>
                <p className="mt-0.5 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {tone.body}
                </p>
            </div>
            <button
                onClick={openPortal}
                disabled={portalBusy}
                className={`${tone.btn} inline-flex items-center gap-1.5 rounded-full text-white text-xs sm:text-sm font-semibold px-4 py-2 whitespace-nowrap disabled:opacity-60`}
                data-testid="trial-banner-portal-btn"
            >
                {portalBusy
                    ? <Loader2 size={13} className="animate-spin" />
                    : <>Add payment method <ExternalLink size={12} /></>
                }
            </button>
        </div>
    );
};

export default TrialBanner;
